import { useEffect, useState, useMemo, useCallback } from "react";
import { api } from "@/api/client";
import { PageHeader, fmtDate, EmptyState } from "@/components/common";
import { Loader2, ArrowDownToLine, ArrowUpFromLine, Search, X, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";

const STATUS_OPTS = {
  incoming: ["received", "failed"],
  outgoing: ["sent", "sandbox-logged", "failed", "pending"],
};

export default function MessageLogs({ direction = "incoming" }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({});

  // filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // column sort (client-side on current page)
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;
      const r = await api.get(`/whatsapp/messages/${direction}`, { params });
      setRows(r.data?.data || []);
      setMeta(r.data?.meta || {});
    } finally { setLoading(false); }
  }, [direction, page, search, statusFilter]);

  useEffect(() => { load(); }, [load]);

  // reset page when filters change
  useEffect(() => { setPage(1); }, [search, statusFilter, direction]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const displayed = useMemo(() => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => {
      const va = a[sortKey] ?? "", vb = b[sortKey] ?? "";
      const cmp = String(va).localeCompare(String(vb));
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [rows, sortKey, sortDir]);

  const SortTh = ({ label, k }) => (
    <th onClick={() => toggleSort(k)} className="cursor-pointer select-none group">
      <div className="flex items-center gap-1.5">{label}
        {sortKey === k ? (sortDir === "asc" ? <ChevronUp size={11} className="text-[#334E7B]"/> : <ChevronDown size={11} className="text-[#334E7B]"/>)
          : <ChevronsUpDown size={11} className="opacity-0 group-hover:opacity-50 transition-opacity"/>}
      </div>
    </th>
  );

  const statusOpts = STATUS_OPTS[direction] || [];

  return (
    <div data-testid={`messages-${direction}-page`}>
      <PageHeader
        title={direction === "incoming" ? "Incoming Messages" : "Outgoing Messages"}
        breadcrumbs={["WhatsApp Control", direction === "incoming" ? "Incoming" : "Outgoing"]}
      />

      {/* Filters */}
      <div className="erp-card p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={`Search number or message...`}
            className="erp-input pl-8 text-sm"
          />
          {search && <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={12}/></button>}
        </div>

        {/* Status filter */}
        <div className="flex rounded-md border border-slate-300 overflow-hidden text-xs font-medium">
          <button onClick={() => setStatusFilter("")}
            className={`px-3 py-2 transition-colors ${statusFilter === "" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
            All
          </button>
          {statusOpts.map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-2 capitalize transition-colors ${statusFilter === s ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              {s.replace("-", " ")}
            </button>
          ))}
        </div>
      </div>

      <div className="card overflow-x-auto">
        <div className="card-head">
          <h3><span className="dot" />{direction === "incoming" ? "Incoming Messages" : "Outgoing Messages"}</h3>
          <span className="text-xs text-slate-500 whitespace-nowrap">{meta?.total ?? rows.length} messages</span>
        </div>
        {loading ? <div className="p-8 text-center"><Loader2 className="animate-spin inline"/></div> :
         rows.length === 0 ? <EmptyState title="No messages" hint={`No ${direction} messages match your filters.`} /> : (
          <table className="erp-table">
            <thead>
              <tr>
                <th className="w-8"></th>
                <SortTh label={direction === "incoming" ? "From" : "To"} k={direction === "incoming" ? "from_number" : "to_number"}/>
                <SortTh label="Message" k="body"/>
                {direction === "incoming" && <th>Parsed Intent</th>}
                <SortTh label="Task" k="task_id"/>
                <SortTh label="Status" k="status"/>
                <SortTh label="Time" k="created_at"/>
              </tr>
            </thead>
            <tbody>
              {displayed.map(m => (
                <tr key={m.id}>
                  <td>{direction === "incoming" ? <ArrowDownToLine size={14} className="text-[#334E7B]"/> : <ArrowUpFromLine size={14} className="text-emerald-600"/>}</td>
                  <td className="font-mono text-xs">{direction === "incoming" ? m.from_number : m.to_number}</td>
                  <td className="font-mono text-xs max-w-[400px] truncate" title={m.body}>{m.body}</td>
                  {direction === "incoming" && (
                    <td>
                      <span className="badge-base bg-violet-50 text-violet-700 border-violet-200">
                        {m.parsed_command?.intent || "—"}
                      </span>
                      {m.parsed_command?.ai_assisted && <span className="badge-base bg-blue-50 text-blue-700 border-blue-200 ml-1">AI</span>}
                    </td>
                  )}
                  <td className="font-mono text-[10px] text-slate-500">{m.parsed_command?.task_code || m.task_id || "—"}</td>
                  <td>
                    <span className={`badge-base ${
                      m.status === "sent" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                      m.status === "failed" ? "bg-red-50 text-red-700 border-red-200" :
                      "bg-slate-50 text-slate-600 border-slate-200"
                    }`}>{m.status || "—"}</span>
                  </td>
                  <td className="text-xs">{fmtDate(m.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {meta?.total_pages > 1 && (
        <div className="flex items-center justify-end gap-2 mt-3 text-sm">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="erp-btn-secondary">Prev</button>
          <span className="text-slate-600">Page {page} / {meta.total_pages}</span>
          <button disabled={page >= meta.total_pages} onClick={() => setPage(p => p + 1)} className="erp-btn-secondary">Next</button>
        </div>
      )}
    </div>
  );
}
